<script setup lang="ts">
import { onClickOutside } from "@vueuse/core";

import type { Continent } from "~/shared/types/continents";

const {
  name = "",
  placeholder = "",
  allIn = "all hotels in {loc}",
  allHotels = "all",
  emptyValue = "click to choose city or hotel",
  hotelsOnly = false,
  disabled = false,
  showValidationIcon = false,
  continents,
} = defineProps<{
  name?: string;
  placeholder?: string;
  allIn?: string;
  allHotels?: string;
  emptyValue?: string;
  continents: Continent[];
  hotelsOnly?: boolean;
  disabled?: boolean;
  showValidationIcon?: boolean;
}>();

const model = defineModel<number | string>();

const showOptions = ref(false);
const selectedLocation = computed(() => {
  if (model.value) {
    return (
      continents.find((c) => c.id === model.value) ||
      continents
        .flatMap((c) => c.cities || [])
        .find((ct) => ct.id === model.value) ||
      continents
        .flatMap((c) => c.cities?.flatMap((ct) => ct.properties || []))
        .find((p) => p?.id === model.value)
    );
  }

  return null;
});
const selectedLocationTitle = computed(
  () => selectedLocation.value?.title || emptyValue,
);
const selectedLocationType = computed(() => selectedLocation.value?.type || "");

const showInputOptions = () => {
  showOptions.value = true;
};
const closeOptions = () => {
  showOptions.value = false;
};

const setLocation = (loc: { id: number | string; type?: string }) => {
  model.value = loc.id;
  closeOptions();
};

const locationSelect = useTemplateRef("location-select");
onClickOutside(locationSelect, () => closeOptions());
</script>

<template>
  <div class="citizem-location-select-wrap" ref="location-select">
    <input type="hidden" :name="name" :value="model ? model : ''" />
    <input type="hidden" name="type" :value="selectedLocationType" />
    <Input
      @focus="showInputOptions()"
      @keydown.enter.prevent="showInputOptions()"
      @keydown.space.prevent="showInputOptions()"
      v-bind="$attrs"
      :modelValue="selectedLocationTitle"
      :placeholder="placeholder"
      :id="`inp_loc_${name}`"
      class="input-location-select"
      readonly
      fullwidth
      :disabled="disabled"
      aria-haspopup="dialog"
      :aria-controls="`modal-locations-${name}`"
      :aria-label="placeholder"
      tabindex="0"
      :tabIndexValue="-1"
      is-selectable
      :validIcon="showValidationIcon"
    >
      <template v-slot:after-icon>
        <IconLocation />
      </template>
    </Input>

    <ModalMobile
      @close="closeOptions"
      :only-mobile="true"
      :full-height="true"
      :visible="showOptions"
      :id="`modal-locations-${name}`"
      role="dialog"
      aria-modal="true"
    >
      <template v-slot:header>
        <H level="3" size="5" class="mt24 mb16">{{ placeholder }}</H>
      </template>
      <template v-slot:default>
        <InputPartsInputLocationTabs
          :hotels-only="hotelsOnly"
          @locationChange="setLocation"
          :continents="continents"
          :all-in="allIn"
          :all-hotels="allHotels"
        />
      </template>
    </ModalMobile>

    <SurfacePaper
      class="location-select-wrap"
      :desktop-only="true"
      :no-padding="true"
      v-show="showOptions"
      :id="`desktop-modal-locations-${name}`"
      role="dialog"
      aria-modal="true"
    >
      <div class="modal-drop-down-options npt fixed-height">
        <InputPartsInputLocationTabs
          :hotels-only="hotelsOnly"
          @locationChange="setLocation"
          :continents="continents"
          :all-in="allIn"
          :all-hotels="allHotels"
        />
      </div>
    </SurfacePaper>
  </div>
</template>

<style lang="scss">
.location-select-wrap {
  position: absolute;
  top: calc(100% + var(--space-16));
  left: 0;
  z-index: 9;
}

.input-location-select {
  .inp {
    padding-right: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: default;
  }
}

.citizem-location-select-wrap {
  position: relative;

  .tabs-header {
    position: relative;
    padding: var(--space-12) 0; // var(--space-12) var(--space-24);
    overflow: hidden;

    @include md {
      left: calc(-1 * var(--space-16));
      width: calc(100% + var(--space-32));
      padding: var(--space-12) 0 var(--space-12) var(--space-24);
    }

    &::after {
      position: absolute;
      top: calc(-1 * var(--space-12));
      left: calc(-1 * var(--space-24));
      z-index: 0;
      display: block;
      width: calc(100% + 48px);
      height: 100%;
      box-shadow: var(--shadow);
      content: "";
      pointer-events: none;
    }
  }
}
</style>
